import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Eye, UserPlus, Users, ShieldCheck, Crown, Star, User as UserIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ADMIRER_ACTION } from "../components/community/AdmirersCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 좌측 카테고리 정의 (값은 백엔드 type과 일치)
const CATEGORIES = [
  { key: "all", label: "전체", Icon: Users },
  { key: "visit", label: "프로필 본 사람", Icon: Eye },
  { key: "follow", label: "팔로우 한 사람", Icon: UserPlus },
  { key: "like", label: "좋아요 한 사람", Icon: Heart },
];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR");
}

function membershipBadge(type) {
  if (type === "vip") return { label: "VIP", cls: "bg-amber-400 text-stone-900", Icon: Crown };
  if (type === "premium") return { label: "프리미엄", cls: "bg-rose-500 text-white", Icon: Star };
  if (["regular", "standard"].includes(type)) return { label: "정회원", cls: "bg-blue-500 text-white", Icon: UserIcon };
  return { label: "일반", cls: "bg-stone-300 text-stone-700", Icon: UserIcon };
}

export default function AdmirersPage() {
  const { token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, today_count: 0, counts: { all: 0, visit: 0, follow: 0, like: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (authLoading) return;
    if (!token) { navigate("/"); return; }
    fetch(`${API_URL}/api/me/admirers?limit=200`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { items: [], total: 0, today_count: 0, counts: { all: 0, visit: 0, follow: 0, like: 0 } }))
      .then(setData)
      .finally(() => setLoading(false));
    // 전체 목록을 열면 '확인'으로 간주 → 미확인 배지 초기화
    fetch(`${API_URL}/api/me/admirers/seen`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [token, authLoading, navigate]);

  const counts = data.counts || { all: data.total, visit: 0, follow: 0, like: 0 };
  const visibleItems = filter === "all" ? data.items : data.items.filter((it) => it.type === filter);

  return (
    <div className="min-h-screen bg-[#F4F2EE] pt-24 pb-24" data-testid="admirers-page">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-burgundy-700 mb-5 text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> 뒤로 가기
        </button>

        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-10 h-10 rounded-xl bg-burgundy-50 flex items-center justify-center">
            <Heart className="w-5 h-5 text-burgundy-600" />
          </div>
          <div>
            <h1 className="font-serif text-2xl text-stone-900">받은 관심</h1>
            <p className="text-sm text-stone-500">
              총 <span className="font-bold text-burgundy-600">{counts.all}</span>명
              {data.today_count > 0 && <> · 오늘 <span className="font-bold text-burgundy-600">{data.today_count}</span>명</>}
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          {/* 좌측 카테고리 */}
          <nav className="w-32 sm:w-44 shrink-0 bg-white rounded-2xl border border-stone-100 p-2" data-testid="admirers-categories">
            {CATEGORIES.map((cat) => {
              const CatIcon = cat.Icon;
              const active = filter === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => setFilter(cat.key)}
                  className={`w-full flex items-center gap-2 px-2.5 sm:px-3 py-2.5 rounded-xl mb-1 text-left transition-colors ${
                    active ? "bg-burgundy-50 text-burgundy-700" : "text-stone-600 hover:bg-stone-50"
                  }`}
                  data-testid={`admirers-cat-${cat.key}`}
                >
                  <CatIcon className={`w-4 h-4 shrink-0 ${active ? "text-burgundy-600" : "text-stone-400"}`} />
                  <span className="flex-1 min-w-0 text-[12.5px] sm:text-sm font-medium truncate">{cat.label}</span>
                  <span className={`text-[12px] font-bold ${active ? "text-burgundy-600" : "text-stone-400"}`}>
                    ({counts[cat.key] || 0})
                  </span>
                </button>
              );
            })}
          </nav>

          {/* 우측 목록 */}
          <div className="flex-1 min-w-0 bg-white rounded-2xl border border-stone-100 divide-y divide-stone-50 overflow-hidden">
            {loading ? (
              <div className="py-20 text-center text-stone-400 text-sm">불러오는 중…</div>
            ) : visibleItems.length === 0 ? (
              <div className="py-20 text-center px-4" data-testid="admirers-page-empty">
                <p className="text-stone-600">아직 해당 활동이 없어요 🌿</p>
                <p className="text-sm text-stone-400 mt-2">따뜻한 이야기를 나누다 보면 인연이 찾아올 거예요.</p>
              </div>
            ) : (
              visibleItems.map((it, idx) => {
                const action = ADMIRER_ACTION[it.type] || ADMIRER_ACTION.visit;
                const ActIcon = action.Icon;
                const badge = membershipBadge(it.membership);
                const BadgeIcon = badge.Icon;
                const src = it.profile_image
                  ? (it.profile_image.startsWith("http") ? it.profile_image : `${API_URL}${it.profile_image}`)
                  : null;
                return (
                  <button
                    key={`${it.actor_id}-${it.type}-${idx}`}
                    onClick={() => navigate(`/timeline/${it.actor_id}`)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-stone-50 transition-colors text-left"
                    data-testid={`admirers-page-row-${it.actor_id}`}
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border border-stone-100">
                      {src ? (
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center bg-gradient-to-br from-burgundy-400 to-rose-400 text-white font-bold">
                          {(it.masked_name || "?").charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-stone-900 text-[15px]">{it.masked_name}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-0.5 ${badge.cls}`}>
                          <BadgeIcon className="w-2.5 h-2.5" />{badge.label}
                        </span>
                        {it.document_verified && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-0.5 bg-cyan-500 text-white" title="서류 인증">
                            <ShieldCheck className="w-2.5 h-2.5" />인증
                          </span>
                        )}
                      </div>
                      <p className="text-[13px] text-stone-500 mt-0.5 truncate">
                        님이 {action.text}
                        {it.type === "like" && it.post_title ? <span className="text-stone-400"> ({it.post_title})</span> : null}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center ${action.bg}`}>
                        <ActIcon className={`w-4 h-4 ${action.color}`} />
                      </span>
                      <span className="text-[10px] text-stone-400">{timeAgo(it.created_at)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
